// src/projects/chatterbox/ChatterboxPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import Navigation from "../../Navigation";
import Footer from "../../Footer";
import ChatterboxDemo from "./chatterboxDemo";
import "./ChatterboxPage.css";

// 导入Go WASM加载器
import { loadGoModule } from "./utils/wasmLoader";

const ChatterboxPage = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState("overview");
  const [wasmLoaded, setWasmLoaded] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const chatterboxDemoRef = useRef(null);

  // 加载Go WASM模块
  useEffect(() => {
    const loadWasm = async () => {
      setIsLoading(true);
      try {
        // 加载Go WASM模块
        await loadGoModule();
        setWasmLoaded(true);
        setLoadingError(null);
      } catch (error) {
        console.error("Failed to load WASM module:", error);
        setLoadingError(error.message);
        setWasmLoaded(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadWasm();
  }, []);

  // 提供一个重试机制
  const retryLoading = async () => {
    setIsLoading(true);
    setLoadingError(null);
    try {
      await loadGoModule();
      setWasmLoaded(true);
    } catch (error) {
      console.error("重试加载WASM模块失败:", error);
      setLoadingError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "Chatterbox: Secure Communication Protocol",
      zh: "Chatterbox: 安全通信协议实现",
      ja: "Chatterbox: セキュア通信プロトコル",
    },
    tagline: {
      en: "End-to-end encrypted messaging system based on Signal Protocol",
      zh: "基于Signal协议的端到端加密通信系统",
      ja: "Signalプロトコルに基づくエンドツーエンド暗号化メッセージングシステム",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "Chatterbox is an instant messaging system implementing forward secrecy and end-to-end encryption based on the Signal/Double-ratchet protocol. This project demonstrates the core principles of modern secure communication protocols, including key exchange, message encryption/decryption, and key rotation mechanisms.",
      zh: "Chatterbox是一个实现了前向安全性和端到端加密的即时通讯系统，基于Signal/Double-ratchet协议设计。本项目展示了现代安全通信协议的核心工作原理，包括密钥交换、消息加密与解密以及密钥轮转机制。",
      ja: "Chatterboxは、Signal/Double-ratchetプロトコルに基づいて前方秘匿性とエンドツーエンド暗号化を実装したインスタントメッセージングシステムです。このプロジェクトでは、鍵交換、メッセージの暗号化/復号化、鍵の更新メカニズムなど、現代の安全な通信プロトコルの中核となる原理を示しています。",
    },
    e2e: {
      en: "End-to-End Encryption",
      zh: "端到端加密",
      ja: "エンドツーエンド暗号化",
    },
    e2eDesc: {
      en: "Using AES-GCM for message encryption, ensuring only the conversation participants can read the message content.",
      zh: "使用AES-GCM进行消息加密，确保只有对话双方能够读取消息内容。",
      ja: "メッセージの暗号化にAES-GCMを使用し、会話の参加者のみがメッセージの内容を読むことができるようにします。",
    },
    forward: {
      en: "Forward Secrecy",
      zh: "前向安全性",
      ja: "前方秘匿性",
    },
    forwardDesc: {
      en: "Even if keys are compromised, previous communications cannot be decrypted, protecting historical conversations.",
      zh: "即使密钥泄露，也无法解密之前的通信内容，保护历史对话安全。",
      ja: "鍵が漏洩しても、過去の通信を復号することはできず、過去の会話が保護されます。",
    },
    keyRotation: {
      en: "Key Rotation",
      zh: "密钥轮转",
      ja: "鍵の更新",
    },
    keyRotationDesc: {
      en: "Implements the Double Ratchet algorithm, enhancing security through frequent key updates.",
      zh: "实现Double Ratchet算法，通过频繁更新密钥提升安全性。",
      ja: "Double Ratchetアルゴリズムを実装し、頻繁な鍵の更新によってセキュリティを強化します。",
    },
    antiReplay: {
      en: "Anti-Replay Protection",
      zh: "抗重放攻击",
      ja: "リプレイ攻撃対策",
    },
    antiReplayDesc: {
      en: "Messages include counters and verification data to prevent interception and replay attacks.",
      zh: "消息包含计数器和验证数据，防止消息被拦截和重新发送。",
      ja: "メッセージにはカウンターと検証データが含まれており、傍受やリプレイ攻撃を防止します。",
    },
    demo: {
      en: "Interactive Demo",
      zh: "交互式演示",
      ja: "インタラクティブデモ",
    },
    demoIntro: {
      en: "The demo below shows how Alice and Bob establish a secure connection and exchange encrypted messages. You can switch identities to send messages and view the underlying protocol process.",
      zh: "下面的演示展示了Alice和Bob如何建立安全连接并交换加密消息。您可以切换身份发送消息，并查看底层协议的运行过程。",
      ja: "以下のデモは、アリスとボブが安全な接続を確立し、暗号化されたメッセージを交換する方法を示しています。アイデンティティを切り替えてメッセージを送信し、基礎となるプロトコルプロセスを表示できます。",
    },
    instructions: {
      en: "Demo Instructions",
      zh: "演示说明",
      ja: "デモ手順",
    },
    step1: {
      en: 'Click "Perform Handshake" to start the three-way handshake process and establish secure communication',
      zh: "点击执行握手开始三次握手过程，建立安全通信",
      ja: "「ハンドシェイクの実行」をクリックして、3ウェイハンドシェイクプロセスを開始し、安全な通信を確立します",
    },
    step2: {
      en: "Switch between Alice and Bob identities to send messages",
      zh: "切换Alice/Bob身份发送消息",
      ja: "アリスとボブのアイデンティティを切り替えてメッセージを送信します",
    },
    step3: {
      en: 'View "Protocol Details" to understand the cryptographic operations behind each step',
      zh: "查看协议细节了解每一步背后的密码学操作",
      ja: "「プロトコルの詳細」を表示して、各ステップの背後にある暗号化操作を理解します",
    },
    step4: {
      en: 'Click "Reset Session" to restart the entire process',
      zh: "点击重置会话可以重新开始整个过程",
      ja: "「セッションのリセット」をクリックして、プロセス全体を再開します",
    },
    techStack: {
      en: "Technology Stack",
      zh: "技术栈",
      ja: "技術スタック",
    },
    acknowledgements: {
      en: "Acknowledgements",
      zh: "致谢",
      ja: "謝辞",
    },
    disclaimer: {
      en: "Note: This code is for educational purposes only and should not be used in security-critical applications.",
      zh: "注意：此代码仅用于教育目的，不应用于安全关键型应用。",
      ja: "注意：このコードは教育目的のみであり、セキュリティが重要なアプリケーションでは使用しないでください。",
    },
    githubLink: {
      en: "View source code on GitHub",
      zh: "在GitHub上查看源代码",
      ja: "GitHubでソースコードを見る",
    },
    loadingError: {
      en: "Failed to load WebAssembly module. This demo requires WebAssembly support.",
      zh: "加载WebAssembly模块失败。此演示需要WebAssembly支持。",
      ja: "WebAssemblyモジュールの読み込みに失敗しました。このデモにはWebAssemblyのサポートが必要です。",
    },
    retry: {
      en: "Retry",
      zh: "重试",
      ja: "再試行",
    },
    loading: {
      en: "Loading WebAssembly module...",
      zh: "正在加载WebAssembly模块...",
      ja: "WebAssemblyモジュールを読み込んでいます...",
    },
  };

  // 技术栈数据
  const techStack = [
    { name: "Go", category: "backend", color: "blue" },
    { name: "React", category: "frontend", color: "cyan" },
    { name: "JavaScript", category: "frontend", color: "yellow" },
    { name: "WebAssembly", category: "platform", color: "orange" },
    { name: "AES-GCM", category: "crypto", color: "emerald" },
    { name: "ECDH", category: "crypto", color: "emerald" },
    { name: "SHA-256", category: "crypto", color: "emerald" },
    { name: "Double Ratchet", category: "protocol", color: "purple" },
    { name: "X3DH", category: "protocol", color: "purple" },
    { name: "Tailwind CSS", category: "frontend", color: "cyan" },
  ];

  // 按类别获取技术标签的颜色
  const getTagColor = (category) => {
    switch (category) {
      case "backend":
        return "bg-blue-100 text-blue-800";
      case "frontend":
        return "bg-cyan-100 text-cyan-800";
      case "crypto":
        return "bg-emerald-100 text-emerald-800";
      case "protocol":
        return "bg-purple-100 text-purple-800";
      case "platform":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6 flex justify-between items-center">
            <Link
              to="/projects"
              className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {translations.backToProjects[language] ||
                translations.backToProjects.en}
            </Link>

            {/* GitHub链接 */}
            <a
              href="https://github.com/nomegustaexam/chatterbox-fall24-security"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="mr-2"
              >
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              {translations.githubLink[language] || translations.githubLink.en}
            </a>
          </div>

          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-light mb-4 text-neutral-800">
              {translations.title[language] || translations.title.en}
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              {translations.tagline[language] || translations.tagline.en}
            </p>

            {/* 技术栈标签 */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className={`${getTagColor(
                    tech.category
                  )} text-xs font-medium px-3 py-1 rounded-full`}
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </header>

          <section className="bg-white p-8 rounded-3xl shadow-sm mb-12">
            <h2 className="text-3xl font-light mb-6 text-neutral-800 border-b border-neutral-100 pb-3">
              {translations.overview[language] || translations.overview.en}
            </h2>
            <p className="text-neutral-600 mb-8 leading-relaxed">
              {translations.description[language] ||
                translations.description.en}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-neutral-50 p-6 rounded-2xl">
                <h3 className="text-xl font-medium mb-3 text-primary-500">
                  {translations.e2e[language] || translations.e2e.en}
                </h3>
                <p className="text-neutral-600">
                  {translations.e2eDesc[language] || translations.e2eDesc.en}
                </p>
              </div>

              <div className="bg-neutral-50 p-6 rounded-2xl">
                <h3 className="text-xl font-medium mb-3 text-primary-500">
                  {translations.forward[language] || translations.forward.en}
                </h3>
                <p className="text-neutral-600">
                  {translations.forwardDesc[language] ||
                    translations.forwardDesc.en}
                </p>
              </div>

              <div className="bg-neutral-50 p-6 rounded-2xl">
                <h3 className="text-xl font-medium mb-3 text-primary-500">
                  {translations.keyRotation[language] ||
                    translations.keyRotation.en}
                </h3>
                <p className="text-neutral-600">
                  {translations.keyRotationDesc[language] ||
                    translations.keyRotationDesc.en}
                </p>
              </div>

              <div className="bg-neutral-50 p-6 rounded-2xl">
                <h3 className="text-xl font-medium mb-3 text-primary-500">
                  {translations.antiReplay[language] ||
                    translations.antiReplay.en}
                </h3>
                <p className="text-neutral-600">
                  {translations.antiReplayDesc[language] ||
                    translations.antiReplayDesc.en}
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white p-8 rounded-3xl shadow-sm mb-12">
            <h2 className="text-3xl font-light mb-6 text-neutral-800 border-b border-neutral-100 pb-3">
              {translations.demo[language] || translations.demo.en}
            </h2>
            <p className="text-neutral-600 mb-8 leading-relaxed">
              {translations.demoIntro[language] || translations.demoIntro.en}
            </p>

            <div className="border border-neutral-100 rounded-2xl overflow-hidden mb-8 chatterbox-demo-wrapper">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
                  <p className="text-neutral-600">
                    {translations.loading[language] || translations.loading.en}
                  </p>
                </div>
              ) : loadingError ? (
                <div className="bg-red-50 p-8 rounded-lg text-center">
                  <div className="text-red-500 mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12 mx-auto"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-red-700 mb-4">
                    {translations.loadingError[language] ||
                      translations.loadingError.en}
                  </p>
                  <div className="text-sm text-red-600 mb-6">
                    {loadingError}
                  </div>
                  <button
                    onClick={retryLoading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    {translations.retry[language] || translations.retry.en}
                  </button>
                </div>
              ) : (
                <ChatterboxDemo ref={chatterboxDemoRef} />
              )}
            </div>

            <div className="bg-neutral-50 p-6 rounded-2xl">
              <h3 className="text-xl font-medium mb-4 text-primary-500">
                {translations.instructions[language] ||
                  translations.instructions.en}
              </h3>
              <ul className="space-y-3 text-neutral-600 list-disc pl-5">
                <li>{translations.step1[language] || translations.step1.en}</li>
                <li>{translations.step2[language] || translations.step2.en}</li>
                <li>{translations.step3[language] || translations.step3.en}</li>
                <li>{translations.step4[language] || translations.step4.en}</li>
              </ul>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl text-amber-800 text-sm">
                <strong>
                  {language === "en"
                    ? "Note:"
                    : language === "zh"
                    ? "注意："
                    : "注意："}
                </strong>{" "}
                {language === "en"
                  ? "This demo uses WebAssembly to run the Go-based cryptographic code directly in your browser. No data is sent to any server."
                  : language === "zh"
                  ? "此演示使用WebAssembly在您的浏览器中直接运行基于Go的加密代码。所有数据都在本地处理，不会发送到任何服务器。"
                  : "このデモはWebAssemblyを使用して、Goベースの暗号化コードをブラウザで直接実行します。データはサーバーに送信されません。"}
              </div>
            </div>
          </section>

          <section className="bg-white p-8 rounded-3xl shadow-sm mb-12">
            <h2 className="text-3xl font-light mb-6 text-neutral-800 border-b border-neutral-100 pb-3">
              {translations.techStack[language] || translations.techStack.en}
            </h2>

            {/* 技术栈可视化 */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
              {techStack.map((tech, index) => (
                <div
                  key={index}
                  className={`flex flex-col items-center justify-center p-6 rounded-2xl ${getTagColor(
                    tech.category
                  )} bg-opacity-20 hover:bg-opacity-30 transition-all duration-300`}
                >
                  <span className="text-lg font-medium mb-2">{tech.name}</span>
                  <span className="text-xs opacity-70 capitalize">
                    {tech.category}
                  </span>
                </div>
              ))}
            </div>

            {/* 项目功能架构图 */}
            <div className="bg-neutral-50 p-6 rounded-2xl">
              <h3 className="text-xl font-medium mb-4 text-primary-500">
                {language === "en"
                  ? "Architecture"
                  : language === "zh"
                  ? "架构概览"
                  : "アーキテクチャ"}
              </h3>
              <div className="flex justify-center items-center p-8">
                <div className="space-y-4 w-full max-w-2xl">
                  <div className="p-4 bg-blue-50 rounded-xl text-center text-blue-800 border border-blue-100">
                    UI Layer (React)
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-neutral-300"></div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl text-center text-orange-800 border border-orange-100">
                    WebAssembly Bridge
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-neutral-300"></div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl text-center text-purple-800 border border-purple-100">
                    Signal Protocol Implementation (Go)
                  </div>
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-neutral-300"></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-emerald-50 rounded-xl text-center text-emerald-800 border border-emerald-100">
                      X3DH
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl text-center text-emerald-800 border border-emerald-100">
                      Double Ratchet
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl text-center text-emerald-800 border border-emerald-100">
                      AES-GCM
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <footer className="text-center text-neutral-500 py-6 border-t border-neutral-100">
            <h3 className="text-xl font-medium mb-4 text-neutral-700">
              {translations.acknowledgements[language] ||
                translations.acknowledgements.en}
            </h3>
            <p>
              {language === "en"
                ? "This project is based on Professor Joseph Bonneau's Signal Protocol educational implementation, with improvements and extensions."
                : language === "zh"
                ? "本项目基于Joseph Bonneau教授的Signal协议教育实现，经过改进和扩展。"
                : "このプロジェクトはJoseph Bonneau教授のSignalプロトコル教育実装を基にして、改良と拡張を加えたものです。"}
              <br />
              <span className="text-neutral-400 mt-2 block">
                {translations.disclaimer[language] ||
                  translations.disclaimer.en}
              </span>
            </p>

            <div className="mt-6 text-sm text-neutral-400">
              {language === "en"
                ? "All cryptographic operations are performed locally in your browser."
                : language === "zh"
                ? "所有加密操作均在您的浏览器中本地执行。"
                : "すべての暗号化操作はブラウザ内でローカルに実行されます。"}
            </div>
          </footer>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ChatterboxPage;
